import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Download, Flag } from 'lucide-react';
import {
  SectionHeading, FilterBar, FilterSearch, FilterToggle,
  Button, StatusBadge, DomainCard, Tooltip, TooltipTrigger, TooltipContent,
  EmptyState, Skeleton, ICON_PX,
} from '../ui';
import { getPMCompletionHistory } from '../../utils/api';

const VIEW_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'completed', label: 'Completed' },
  { value: 'skipped', label: 'Skipped' },
  { value: 'overdue', label: 'Overdue' },
  { value: 'flagged', label: 'Flagged' },
];

function PMReportingTab({ readOnly = false, initialFilter }) {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [viewFilter, setViewFilter] = useState(initialFilter?.view || 'all');

  useEffect(() => {
    setLoading(true);
    getPMCompletionHistory('unit-main-st').then((data) => {
      setHistory(data);
      setLoading(false);
    });
  }, []);

  const filtered = useMemo(() => {
    let result = history;
    if (search) {
      const q = search.toLowerCase();
      result = result.filter((r) => r.taskName.toLowerCase().includes(q) || r.domain.toLowerCase().includes(q));
    }
    if (viewFilter === 'completed') result = result.filter((r) => !r.skipIndicator && !r.overdueIndicator);
    if (viewFilter === 'skipped') result = result.filter((r) => r.skipIndicator);
    if (viewFilter === 'overdue') result = result.filter((r) => r.overdueIndicator);
    if (viewFilter === 'flagged') result = result.filter((r) => r.skipCount30d >= 3);
    return result;
  }, [history, search, viewFilter]);

  // Compute trend data for charts
  const trendData = useMemo(() => {
    if (!history.length) return { completion: [], skip: [] };
    // Group by week (simplified: last 4 data points)
    const weeks = ['W-3', 'W-2', 'W-1', 'Now'];
    const chunkSize = Math.ceil(history.length / 4);
    const completion = weeks.map((w, i) => {
      const chunk = history.slice(i * chunkSize, (i + 1) * chunkSize);
      const completed = chunk.filter((r) => !r.skipIndicator && !r.overdueIndicator).length;
      const total = chunk.length || 1;
      return { name: w, value: Math.round((completed / total) * 100) };
    });
    const skip = weeks.map((w, i) => {
      const chunk = history.slice(i * chunkSize, (i + 1) * chunkSize);
      const skipped = chunk.filter((r) => r.skipIndicator).length;
      const total = chunk.length || 1;
      return { name: w, value: Math.round((skipped / total) * 100) };
    });
    return { completion, skip };
  }, [history]);

  const handleExport = useCallback(() => {
    const headers = ['Task Name', 'Domain', 'Due Date', 'Completion Date', 'Time (min)', 'Completed By', 'Type', 'Note', 'Overdue', 'Skipped', 'Skip Count (30d)'];
    const rows = filtered.map((r) => [
      r.taskName, r.domain, r.dueDate, r.completionDate || '', r.timeToCompletion || '',
      r.completedBy || '', r.completionType, r.note || '',
      r.overdueIndicator ? 'Yes' : 'No', r.skipIndicator ? 'Yes' : 'No', r.skipCount30d,
    ]);
    const csv = [headers.join(','), ...rows.map((r) => r.map((c) => `"${c}"`).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `task-completion-report-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [filtered]);

  if (loading) {
    return (
      <div className="space-y-3 py-3">
        <Skeleton.TableRow />
        <Skeleton.TableRow />
        <Skeleton.TableRow />
      </div>
    );
  }

  return (
    <div className="space-y-3 py-3">
      <SectionHeading
        size="sm"
        title="Completion History"
        className="mb-1.5"
        info="Historical task completion records. Tasks skipped 3+ times in 30 days are flagged for review. Export to CSV for compliance reporting."
        action={
          <Button size="sm" variant="outline" onClick={handleExport}>
            <Download size={ICON_PX.md} className="mr-1" /> Export CSV
          </Button>
        }
      />

      <FilterBar align="between">
        <FilterSearch value={search} onChange={setSearch} placeholder="Search..." width="md" />
        <FilterToggle value={viewFilter} onChange={setViewFilter} options={VIEW_FILTERS} />
      </FilterBar>

      {/* Trend Charts */}
      {history.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
          <DomainCard domain="quality" accentPlacement="top" density="compact">
            <div className="p-2">
              <span className="text-caption font-bold text-text-muted uppercase tracking-wider">Completion Rate Trend</span>
              <div className="flex items-end gap-3 mt-2 h-16">
                {trendData.completion.map((d, i) => (
                  <div key={i} className="flex-1 flex flex-col items-center gap-0.5">
                    <div className="w-full bg-nn-border rounded-sm overflow-hidden" style={{ height: 48 }}>
                      <div
                        className="w-full bg-green-nn rounded-sm transition-all"
                        style={{ height: `${d.value}%`, marginTop: `${100 - d.value}%` }}
                      />
                    </div>
                    <span className="text-[9px] text-text-muted">{d.name}</span>
                    <span className="text-caption font-medium text-text-dark">{d.value}%</span>
                  </div>
                ))}
              </div>
            </div>
          </DomainCard>
          <DomainCard domain="practice" accentPlacement="top" density="compact">
            <div className="p-2">
              <span className="text-caption font-bold text-text-muted uppercase tracking-wider">Skip Rate Trend</span>
              <div className="flex items-end gap-3 mt-2 h-16">
                {trendData.skip.map((d, i) => (
                  <div key={i} className="flex-1 flex flex-col items-center gap-0.5">
                    <div className="w-full bg-nn-border rounded-sm overflow-hidden" style={{ height: 48 }}>
                      <div
                        className="w-full bg-yellow-nn rounded-sm transition-all"
                        style={{ height: `${d.value}%`, marginTop: `${100 - d.value}%` }}
                      />
                    </div>
                    <span className="text-[9px] text-text-muted">{d.name}</span>
                    <span className="text-caption font-medium text-text-dark">{d.value}%</span>
                  </div>
                ))}
              </div>
            </div>
          </DomainCard>
        </div>
      )}

      {filtered.length === 0 ? (
        <EmptyState title="No records" body="No completion records match your filters." size="sm" />
      ) : (
        <div className="rounded-lg border border-nn-border overflow-hidden">
          <table className="w-full text-body">
            <thead>
              <tr className="border-b border-nn-border">
                <th className="text-left px-3 py-2 text-caption font-bold text-text-muted uppercase tracking-wider">Task</th>
                <th className="text-left px-3 py-2 text-caption font-bold text-text-muted uppercase tracking-wider">Domain</th>
                <th className="text-center px-3 py-2 text-caption font-bold text-text-muted uppercase tracking-wider">Due</th>
                <th className="text-center px-3 py-2 text-caption font-bold text-text-muted uppercase tracking-wider">Completed</th>
                <th className="text-center px-3 py-2 text-caption font-bold text-text-muted uppercase tracking-wider">Time</th>
                <th className="text-left px-3 py-2 text-caption font-bold text-text-muted uppercase tracking-wider">By</th>
                <th className="text-center px-3 py-2 text-caption font-bold text-text-muted uppercase tracking-wider">Status</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.id} className="border-b border-nn-border last:border-b-0 hover:bg-nn-bg-subtle">
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-1.5">
                      <span className="text-text-dark">{r.taskName}</span>
                      {r.skipCount30d >= 3 && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span>
                              <Flag size={ICON_PX.sm} className="text-yellow-600" />
                            </span>
                          </TooltipTrigger>
                          <TooltipContent side="bottom" maxWidth={180}>
                            Skipped {r.skipCount30d} times in 30 days — review for removal or reassignment
                          </TooltipContent>
                        </Tooltip>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-text-secondary capitalize">{r.domain}</td>
                  <td className="px-3 py-2 text-center text-text-secondary">{r.dueDate}</td>
                  <td className="px-3 py-2 text-center text-text-secondary">{r.completionDate || '—'}</td>
                  <td className="px-3 py-2 text-center text-text-secondary">{r.timeToCompletion ? `${r.timeToCompletion}m` : '—'}</td>
                  <td className="px-3 py-2 text-text-secondary">{r.completedBy || '—'}</td>
                  <td className="px-3 py-2 text-center">
                    {r.skipIndicator ? (
                      <StatusBadge variant="watch">Skipped</StatusBadge>
                    ) : r.overdueIndicator ? (
                      <StatusBadge variant="critical">Overdue</StatusBadge>
                    ) : (
                      <StatusBadge variant="on-track">Complete</StatusBadge>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Summary stats */}
      <div className="flex items-center gap-4 text-body-sm text-text-muted pt-2">
        <span>{filtered.length} records shown</span>
        <span>{filtered.filter((r) => r.skipIndicator).length} skipped</span>
        <span>{filtered.filter((r) => r.overdueIndicator).length} overdue</span>
        <span>{filtered.filter((r) => r.skipCount30d >= 3).length} flagged for review</span>
      </div>
    </div>
  );
}

export default PMReportingTab;
